import styled from "@emotion/styled";
import { MouseEvent, useRef } from "react";
import Counter from "../counter/Counter";
import { KmsTypePresenter } from "@kaltura/mediaspace-shared-types";
import EventPresenterLarge from "../event-presenter-large/EventPresenterLarge";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import EventPresenter from "../event-presenter/EventPresenter";
import { Breakpoint } from "@mui/material";

export interface EventPresenterLargeListProps {
    /**
     * full presenters list
     */
    presenters: KmsTypePresenter[];

    /**
     * callback for the show-more button
     */
    onShowMore?: () => void;

    /**
     * classname for the root element
     */
    className?: string;

    /**
     * show presenter modal on click
     */
    showPresenterModal?: boolean;

    /**
     * breakpoint for showing 3 presenters in a row (instead of 2)
     */
    moreItemsBreakpoint?: Breakpoint | number;

    /**
     * breakpoint for aligning the presenters vertically
     */
    verticalBreakpoint?: Breakpoint | number;

    /**
     * show large presenters on desktop
     */
    showLargePresenters?: boolean;
}

const StyledEventPresentersList = styled("div")<{
    itemsCount: number;
    hasMore: boolean;
    verticalBreakpoint: Breakpoint | number;
}>(({ theme, itemsCount, hasMore, verticalBreakpoint }) => ({
    display: "flex",
    gap: theme.spacing(1),
    flexDirection: "column",
    [theme.breakpoints.up(verticalBreakpoint)]: {
        display: "grid",
        gap: theme.spacing(2),
        gridTemplateColumns: `repeat(${itemsCount}, minmax(0, 1fr)) ${hasMore ? "auto" : ""}`,
    },
}));

const getPresentersElements = (presenters: KmsTypePresenter[], isTinyScreen: boolean, showPresenterModal: boolean) => {
    const Component = isTinyScreen ? EventPresenter : EventPresenterLarge;
    return presenters.map((presenter: KmsTypePresenter) => {
        return <Component key={"presenter" + presenter.id} data={presenter} showPresenterModal={showPresenterModal} />;
    });
};

export function EventPresenterLargeList({
    presenters,
    onShowMore,
    className,
    showPresenterModal = false,
    moreItemsBreakpoint = "lg",
    verticalBreakpoint = "sm",
    showLargePresenters: showLargePresentersProp = true,
}: EventPresenterLargeListProps) {
    const handleClick = (ev: MouseEvent<HTMLDivElement>) => {
        if (onShowMore) {
            onShowMore();
        }
    };
    const counterRef = useRef(null);
    const theme = useTheme();

    const show3Presenters = useMediaQuery(theme.breakpoints.up(moreItemsBreakpoint));
    const isHorizontal = useMediaQuery(theme.breakpoints.up(verticalBreakpoint));
    const showLargePresenters = showLargePresentersProp && isHorizontal;

    const visiblePresentersCount = show3Presenters ? 3 : 2;
    const visiblePresenters = presenters.slice(0, visiblePresentersCount);
    const presentersElements = getPresentersElements(visiblePresenters, !showLargePresenters, showPresenterModal);

    const additionalPresenters = presenters.slice(visiblePresentersCount);
    const hasMore = additionalPresenters.length > 0;

    return (
        <StyledEventPresentersList
            className={className}
            itemsCount={presentersElements.length}
            hasMore={hasMore}
            verticalBreakpoint={verticalBreakpoint}
        >
            {presentersElements}
            {hasMore && (
                <Counter
                    ref={counterRef}
                    data={additionalPresenters.length}
                    onClick={handleClick}
                    className={"kms-ds-event-presenters-more"}
                    variant={"normal"}
                    size={showLargePresenters ? "medium" : "small"}
                    clickable={showPresenterModal}
                />
            )}
        </StyledEventPresentersList>
    );
}

export default EventPresenterLargeList;
